/**
 * Message Service
 *
 * Provides API methods for storing encrypted messages.
 * Used in E2E encryption mode where client encrypts and stores messages
 * after receiving AI responses.
 */
import { apiClient } from '../lib/apiClient';

export interface MessagePart {
  type: 'text' | 'answer' | 'thinking' | 'tool_call' | 'tool_result' | 'citation';
  content: string;
}

export interface MessageCreate {
  role: 'user' | 'assistant';
  content?: string;
  message_parts?: MessagePart[];
  metadata?: string; // Encrypted JSON string
  encrypted: boolean;
}

export interface StoreMessagesResponse {
  stored: number;
  message_ids: string[];
}

export interface UpdateMessageRequest {
  content?: string;
  encrypted?: boolean;
}

/**
 * Store messages for a conversation.
 * Used after AI inference to store encrypted user message and assistant response.
 */
export async function storeMessages(
  conversationId: string,
  messages: MessageCreate[]
): Promise<StoreMessagesResponse> {
  return apiClient.post(`/api/conversations/${conversationId}/messages`, {
    messages,
  });
}

/**
 * Update a message (e.g., for migration from unencrypted to encrypted).
 */
export async function updateMessage(
  conversationId: string,
  messageId: string,
  update: UpdateMessageRequest
): Promise<{ success: boolean }> {
  return apiClient.patch(
    `/api/conversations/${conversationId}/messages/${messageId}`,
    update
  );
}

/**
 * Helper to prepare a user message for storage.
 */
export function createUserMessage(
  encryptedContent: string,
  encrypted: boolean = true,
  metadata?: string,
): MessageCreate {
  return {
    role: 'user',
    content: encryptedContent,
    metadata,
    encrypted,
  };
}

/**
 * Helper to prepare an assistant message for encrypted storage.
 */
export function createAssistantMessage(
  encryptedContent: string,
  encryptedMetadata?: string,
  encrypted: boolean = true
): MessageCreate {
  return {
    role: 'assistant',
    content: encryptedContent,
    metadata: encryptedMetadata,
    encrypted,
  };
}

/**
 * Delete a message from a conversation.
 * Used to clean up orphaned user messages before retry.
 */
export async function deleteMessage(
  conversationId: string,
  messageId: string,
): Promise<{ success: boolean }> {
  return apiClient.delete(`/api/conversations/${conversationId}/messages/${messageId}`);
}

export const messageService = {
  storeMessages,
  updateMessage,
  deleteMessage,
  createUserMessage,
  createAssistantMessage,
};
